// src/Services/docOpeners.js
/**
 * Cross‑platform helpers to open locally generated Excel (SheetJS/xlsx)
 * and PDF (pdfmake) files. On Android (Capacitor) they save to cache
 * and open with the native viewer. On Web/Electron they fall back to
 * a standard download using the existing window.openFile() helper.
 *
 * Guidelines compliance:
 * - No hardcoded URLs (local generation only).
 * - No component axios usage introduced.
 * - Junior‑friendly, documented, small functions.
 */

import { Capacitor } from '@capacitor/core';
import { openPdfNative, openExcelNative } from './nativeOpeners';

/**
 * Ensure a filename has the desired extension.
 * @param {string} name
 * @param {string} ext e.g. 'pdf' | 'xlsx'
 */
function withExt(name, ext) {
  if (!name) return `document.${ext}`;
  const lower = String(name).toLowerCase();
  return (lower.endsWith(`.${ext}`)) ? name : `${name}.${ext}`;
}

/**
 * Open a PDF from a Blob | ArrayBuffer | base64 string.
 * Android → native viewer; Web/Electron → download via window.openFile.
 * @param {Blob|ArrayBuffer|string} input
 * @param {string} filename e.g., 'report.pdf'
 */
export async function openLocalPdf(input, filename = 'report.pdf') {
  const finalName = withExt(filename, 'pdf');
  const platform = Capacitor.getPlatform?.() || 'web';
  if (platform === 'android') {
    return openPdfNative(input, finalName);
  }
  // Web/Electron: route through the shared helper which handles download
  window.openFile({ data: input, headers: { 'content-type': 'application/pdf' } }, finalName);
}

/**
 * Open an Excel workbook using SheetJS (xlsx) output.
 * Pass either:
 *  - An ArrayBuffer (preferred), from XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
 *  - A Blob
 *  - A base64 string
 * Android → native viewer; Web/Electron → download via window.openFile.
 * @param {ArrayBuffer|Blob|string} input
 * @param {string} filename e.g., 'report.xlsx'
 */
export async function openLocalXlsx(input, filename = 'report.xlsx') {
  const finalName = withExt(filename, 'xlsx');
  const platform = Capacitor.getPlatform?.() || 'web';
  if (platform === 'android') {
    return openExcelNative(input, finalName);
  }
  // Web/Electron: route through the shared helper which handles download
  window.openFile({ data: input, headers: { 'content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } }, finalName);
}

/**
 * Convenience: Accept a SheetJS workbook object and open it.
 * Avoids duplicating write options at call sites.
 * @param {any} workbook SheetJS workbook instance
 * @param {string} filename e.g., 'report.xlsx'
 */
export async function openWorkbook(workbook, filename = 'report.xlsx') {
  // Lazy import to avoid bundling if unused in some builds
  let XLSX;
  try {
    XLSX = await import(/* webpackChunkName: "sheetjs-chunk" */ 'xlsx');
  } catch (e) {
    // If dynamic import fails, try global (in case of CDN or prior import)
    XLSX = (window && window.XLSX) ? window.XLSX : null;
  }
  if (!XLSX) throw new Error('xlsx library not available. Ensure it is installed or globally loaded.');
  const ab = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return openLocalXlsx(ab, filename);
}

/**
 * Convenience: Accept a pdfmake docDefinition and open it.
 * Uses getBlob on Android (Blob → native open). On Web/Electron,
 * delegates to window.openFile for a download.
 * @param {any} docDefinition pdfmake docDefinition JSON
 * @param {string} filename e.g., 'report.pdf'
 */
export async function openPdfFromPdfMake(docDefinition, filename = 'report.pdf') {
  const finalName = withExt(filename, 'pdf');
  const platform = Capacitor.getPlatform?.() || 'web';

  // Try to access pdfMake via dynamic import, otherwise fall back to global
  let pdfMake;
  try {
    pdfMake = (await import(/* webpackChunkName: "pdfmake-chunk" */ 'pdfmake/build/pdfmake')).default || (await import('pdfmake/build/pdfmake')).default;
    // Load fonts if available
    try {
      const fonts = await import('pdfmake/build/vfs_fonts');
      if (fonts && fonts.pdfMake && fonts.pdfMake.vfs) {
        pdfMake.vfs = fonts.pdfMake.vfs;
      }
    } catch (_ignored) {}
  } catch (_e) {
    pdfMake = (window && window.pdfMake) ? window.pdfMake : null;
  }

  if (!pdfMake) throw new Error('pdfmake is not available. Ensure it is installed or globally loaded.');

  if (platform === 'android') {
    // Use Blob path for stability on Android
    await new Promise((resolve, reject) => {
      pdfMake.createPdf(docDefinition).getBlob(async (blob) => {
        try {
          await openPdfNative(blob, finalName);
          resolve();
        } catch (err) { reject(err); }
      });
    });
    return;
  }

  // Web/Electron
  await new Promise((resolve, reject) => {
    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      try {
        window.openFile({ data: blob, headers: { 'content-type': 'application/pdf' } }, finalName);
        resolve();
      } catch (err) { reject(err); }
    });
  });
}
